import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { safeOnSnapshot } from "../lib/safeSnapshot";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import { FaBell, FaRegBell } from "react-icons/fa";
import { registerPushToken, listenForegroundMessages } from "../lib/messaging";

const toMillis = (ts) =>
  ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : 0);

const timeAgo = (ts) => {
  const ms = toMillis(ts);
  if (!ms) return "";
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const currentUid = auth.currentUser?.uid;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Notificações do usuário atual (ordenadas no cliente p/ evitar índice composto).
  useEffect(() => {
    if (!currentUid) return undefined;
    const q = query(
      collection(db, "notifications"),
      where("toUid", "==", currentUid)
    );
    const unsub = safeOnSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setItems(list.slice(0, 30));
      },
      {
        label: "notifications",
        onError: (err) => console.error("Erro notificações:", err),
      }
    );
    return () => unsub();
  }, [currentUid]);

  // Push do navegador (FCM): registra token e escuta mensagens em 1º plano.
  useEffect(() => {
    if (!currentUid) return undefined;
    registerPushToken(currentUid);
    let unsub = () => {};
    listenForegroundMessages(() => {
      // O snapshot do Firestore já atualiza a lista; nada extra necessário.
    }).then((fn) => {
      if (fn) unsub = fn;
    });
    return () => unsub();
  }, [currentUid]);

  // Fechar ao clicar fora.
  useEffect(() => {
    const onClick = (e) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await updateDoc(doc(db, "notifications", n.id), { read: true });
    } catch (err) {
      console.error("Erro ao marcar lida:", err);
    }
  };

  const markAllRead = async () => {
    const unreadItems = items.filter((n) => !n.read);
    if (!unreadItems.length) return;
    try {
      const batch = writeBatch(db);
      unreadItems.forEach((n) =>
        batch.update(doc(db, "notifications", n.id), { read: true })
      );
      await batch.commit();
    } catch (err) {
      console.error("Erro ao marcar todas:", err);
    }
  };

  const handleClick = async (n) => {
    await markRead(n);
    setOpen(false);
    navigate(n.link || "/employee/files");
  };

  if (!currentUid) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 ${
          open
            ? "bg-slate-200 text-slate-600"
            : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
        }`}
        title="Notificações"
      >
        {unread > 0 ? (
          <FaBell className="w-[18px] h-[18px] text-blue-600" />
        ) : (
          <FaRegBell className="w-[18px] h-[18px]" />
        )}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-white rounded-xl shadow-lg border border-slate-100 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-800">
              Notificações
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] text-blue-600 hover:underline font-medium"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">
                Nenhuma notificação.
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition flex gap-2.5 ${
                    n.read ? "" : "bg-blue-50/40"
                  }`}
                >
                  <span
                    className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                      n.read ? "bg-transparent" : "bg-blue-500"
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-slate-800 truncate">
                        {n.title || "Notificação"}
                      </span>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">
                        {timeAgo(n.createdAt)}
                      </span>
                    </span>
                    <span className="block text-xs text-slate-600 mt-0.5 break-words">
                      {n.body}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
